import { FC, Dispatch, useState, useEffect } from "react";
import {
  FormatDateFE,
  UtcTimeToLocalHour,
} from "../../../helper/general/methods";
import { UserDecisionInterface } from "../../../middleware/interfaces/user/user-decision-interface";
import { UserInterface } from "../../../middleware/interfaces/user/user-interface";
import { ForgeInterface } from "../../../middleware/interfaces/forge/forge-interface";
import { ForgeDeleteInterface } from "../../../middleware/interfaces/forge/forge-delete-interface";
import { ForgeRunInterface } from "../../../middleware/interfaces/forge/forge-run-interface";
import MenuButton from "../user/menu-button";

interface forgeInfoProps {
  user: UserInterface;
  selectedForge: ForgeInterface;
  setSelectedForge: Dispatch<any>;
  setForgeDialogReveal: Dispatch<any>;
  dispatch: Dispatch<any>;
}

const FORGE_FREQUENCY_LABELS: Record<string, string> = {
  DAILY: "Daily",
  EVERY_3_DAYS: "Every 3 days",
  WEEKLY: "Weekly",
};

const FORGE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  PAUSED: "Paused",
  BROKEN: "Broken",
};

// The stored time is UTC; this is the viewer's own clock, matching the dialog.
const FormatRunTime = (utcTime: string): string => {
  const hour = UtcTimeToLocalHour(utcTime);
  const suffix = hour < 12 ? "AM" : "PM";
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelveHour}:00 ${suffix}`;
};

const FORGE_RUN_STATUS_LABELS: Record<string, string> = {
  SUCCESS: "Succeeded",
  PARTIAL: "Some files skipped",
  FAILED: "Failed",
};

const ForgeInfo: FC<forgeInfoProps> = ({
  user,
  selectedForge,
  setSelectedForge,
  setForgeDialogReveal,
  dispatch,
}) => {
  const [forgeEdit, setForgeEdit] = useState(false);
  const [deleteForgeDialogReveal, setDeleteForgeDialogReveal] = useState(false);
  const [runForgeDialogReveal, setRunForgeDialogReveal] = useState(false);

  useEffect(() => {
    if (forgeEdit === true) {
      setForgeDialogReveal("EDIT");
    }
    setForgeEdit(false);
  }, [forgeEdit]);

  useEffect(() => {
    if (deleteForgeDialogReveal) {
      const forgeDeleteData: ForgeDeleteInterface = {
        email: user.email,
        projectID: user.openProject?.projectID!,
        projectUserRole: user.openProject?.projectUserRole!,
        forgeID: selectedForge.forgeID,
        type: "ForgeDeleteInterface",
      };
      const decision: UserDecisionInterface = {
        title: `Delete ${selectedForge.forgeName}?`,
        message:
          "The forge stops running and its settings are removed. Models it already pulled in stay where they are.",
        confirmLabel: "Delete",
        decisionOptions: ["DELETE_FORGE", "NO_ACTION"],
        result: "",
        data: forgeDeleteData,
        type: "UserDecisionInterface",
      };
      dispatch({ type: "DECISION", decision: decision });
    }
    setDeleteForgeDialogReveal(false);
  }, [deleteForgeDialogReveal]);

  useEffect(() => {
    if (runForgeDialogReveal) {
      const forgeRunData: ForgeRunInterface = {
        email: user.email,
        projectID: user.openProject?.projectID!,
        projectUserRole: user.openProject?.projectUserRole!,
        forgeID: selectedForge.forgeID,
        type: "ForgeRunInterface",
      };
      const decision: UserDecisionInterface = {
        title: `Run ${selectedForge.forgeName} now?`,
        message:
          "The forge is queued and starts within about fifteen minutes. A large folder can take a while to transfer.",
        confirmLabel: "Run now",
        decisionOptions: ["RUN_FORGE", "NO_ACTION"],
        result: "",
        data: forgeRunData,
        type: "UserDecisionInterface",
      };
      dispatch({ type: "DECISION", decision: decision });
    }
    setRunForgeDialogReveal(false);
  }, [runForgeDialogReveal]);

  return (
    <>
      {(() => {
        if (selectedForge) {
          return (
            <div className="itemDetails">
              <div className="itemDetailsHeader">
                <img
                  className="icon-medium"
                  src="assets/icons/forge.png"
                  draggable="false"
                  alt={selectedForge.forgeName}
                />
                <div className="itemDetailsIdentity">
                  <span className="itemDetailsName">
                    {selectedForge.forgeName}
                  </span>
                  <span className="itemDetailsSubtitle">
                    Forge ·{" "}
                    {FORGE_STATUS_LABELS[selectedForge.forgeStatus] ??
                      selectedForge.forgeStatus}
                  </span>
                </div>
                <span
                  className="itemDetailsClose"
                  onClick={() => {
                    setSelectedForge(null);
                  }}
                >
                  ×
                </span>
              </div>
              <div className="itemDetailsMetadata">
                <span className="itemDetailsLabel">ORIGIN</span>
                <span className="itemDetailsValue">
                  {selectedForge.forgeOrigin}
                </span>
                <span className="itemDetailsLabel">DESTINATION</span>
                <span className="itemDetailsValue">
                  {selectedForge.forgeDestination}
                </span>
                <span className="itemDetailsLabel">FREQUENCY</span>
                <span className="itemDetailsValue">
                  {FORGE_FREQUENCY_LABELS[selectedForge.forgeRunFrequency] ??
                    selectedForge.forgeRunFrequency}
                  {" at "}
                  {FormatRunTime(selectedForge.forgeRunTime)}
                </span>
                <span className="itemDetailsLabel">CREATED</span>
                <span className="itemDetailsValue">
                  {FormatDateFE(selectedForge.forgeCreationDate!, true)}
                </span>
                {selectedForge.forgeRunRequested ? (
                  <>
                    <span className="itemDetailsLabel">QUEUED</span>
                    <span className="itemDetailsValue itemDetailsValuePending">
                      Requested{" "}
                      {FormatDateFE(selectedForge.forgeRunRequested, true)} ·
                      starts at the next check
                    </span>
                  </>
                ) : (
                  <></>
                )}
                <span className="itemDetailsLabel">LAST RUN</span>
                <span
                  className={
                    selectedForge.forgeLastRunStatus &&
                    selectedForge.forgeLastRunStatus !== "SUCCESS"
                      ? "itemDetailsValue itemDetailsValueAttention"
                      : "itemDetailsValue"
                  }
                >
                  {/* The date alone used to stand here, which read as "it ran,
                      so it worked". It is the outcome that says that. */}
                  {selectedForge.forgeLastRunDate
                    ? FormatDateFE(selectedForge.forgeLastRunDate, true) +
                      (selectedForge.forgeLastRunStatus
                        ? " · " +
                          (FORGE_RUN_STATUS_LABELS[
                            selectedForge.forgeLastRunStatus
                          ] ?? selectedForge.forgeLastRunStatus)
                        : "")
                    : "—"}
                </span>
                {selectedForge.forgeLastRunError ? (
                  <>
                    <span className="itemDetailsLabel">PROBLEM</span>
                    <span className="itemDetailsValue itemDetailsValueAttention">
                      {selectedForge.forgeLastRunError}
                    </span>
                  </>
                ) : (
                  <></>
                )}
                <span className="itemDetailsLabel">DESCRIPTION</span>
                <span className="itemDetailsValue">
                  {selectedForge.forgeDescription || "—"}
                </span>
              </div>
              <div className="itemDetailsActions">
                {/* A run spends the owner's storage and platform connection, so
                    it is owner-only like the rest. Hidden while paused: the
                    endpoint refuses it, and offering a button that always
                    refuses is worse than not offering one. */}
                {user.openProject?.projectUserRole === "OWNER" &&
                selectedForge.forgeStatus !== "PAUSED" &&
                !selectedForge.forgeRunRequested ? (
                  <MenuButton
                    imageSource="assets/icons/enter.png"
                    alternativeText="Run now"
                    buttonSize="small"
                    buttonAccent="primary"
                    dispatch={() => {
                      setRunForgeDialogReveal(true);
                    }}
                  />
                ) : (
                  <></>
                )}
                {user.openProject?.projectUserRole === "OWNER" ? (
                  <MenuButton
                    imageSource="assets/icons/edit.png"
                    alternativeText="Edit"
                    buttonSize="small"
                    dispatch={() => {
                      setForgeEdit(true);
                    }}
                  />
                ) : (
                  <></>
                )}
                {user.openProject?.projectUserRole === "OWNER" ? (
                  <MenuButton
                    imageSource="assets/icons/delete.png"
                    alternativeText="Delete"
                    buttonSize="small"
                    buttonAccent="destructive"
                    dispatch={() => {
                      setDeleteForgeDialogReveal(true);
                    }}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
          );
        } else {
          return <></>;
        }
      })()}
    </>
  );
};

export default ForgeInfo;
